import { useState, useEffect } from "react";
import { health, countStatuses } from "./dashboardHelpers.js";

// ══════════════════════════════════════════════════════
//  Petits composants visuels partagés par tous les onglets du tableau de bord
// ══════════════════════════════════════════════════════

/** Pastille ronde de statut. */
export function StatusDot({ status, size = 9 }) {
  return <span style={{ width: size, height: size, borderRadius: "50%", background: health(status).color, flexShrink: 0, display: "inline-block" }} />;
}

/** Badge de statut (OK / Avertissement / Critique). */
export function StatusBadge({ status, label }) {
  const h = health(status);
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, whiteSpace: "nowrap",
      textTransform: "uppercase", letterSpacing: ".03em", color: h.color, background: h.bg,
    }}>
      {label || h.label}
    </span>
  );
}

/** Jauge horizontale. */
export function Meter({ label, value, color, right, height = 6 }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4, gap: 8 }}>
        <span style={{ color: "var(--text-3)" }}>{label}</span>
        <span style={{ color, fontWeight: 700 }}>{right ?? (value == null ? "—" : `${value}%`)}</span>
      </div>
      <div style={{ height, borderRadius: 4, background: "var(--navy-soft)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, transition: "width .3s" }} />
      </div>
    </div>
  );
}

/** Carte d'appareil avec liseré gauche coloré selon la gravité. */
export function DeviceCard({ status, children, style }) {
  const h = health(status);
  return (
    <div style={{
      borderRadius: 12, padding: "14px 16px", minWidth: 0,
      background: status === "CRITICAL" ? "var(--danger-dim)" : "var(--card-bg)",
      border: "1px solid var(--border)",
      borderLeft: `4px solid ${status === "OK" ? "var(--success)" : h.color}`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/** En-tête de carte : pastille + nom + sous-titre + badge. */
export function DeviceHead({ status, name, sub, badge, badgeLabel }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
      <StatusDot status={status} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name || "—"}</div>
        {sub && <div style={{ fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
      </div>
      {badge !== false && <StatusBadge status={status} label={badgeLabel} />}
    </div>
  );
}

/** Bloc de faits clé/valeur sur deux colonnes. */
export function Facts({ items, bordered = true }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 6, columnGap: 10, fontSize: 11.5, color: "var(--text-2)",
      ...(bordered ? { paddingBottom: 11, marginBottom: 11, borderBottom: "1px solid var(--border)" } : {}),
    }}>
      {items.map(([k, v, color], i) => (
        <div key={i} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <span style={{ color: "var(--text-3)" }}>{k} : </span>
          <span style={color ? { color, fontWeight: 600 } : undefined}>{v ?? "—"}</span>
        </div>
      ))}
    </div>
  );
}

/** Bandeau d'erreur de synchronisation (identifiants refusés, appareil injoignable…). */
export function SyncError({ message }) {
  if (!message) return null;
  return (
    <div style={{
      display: "flex", gap: 7, alignItems: "flex-start", fontSize: 11.5, color: "var(--danger)",
      background: "var(--danger-dim)", borderRadius: 8, padding: "7px 10px", marginBottom: 10,
    }}>
      <i className="bi bi-exclamation-triangle" style={{ marginTop: 2 }} />
      <span style={{ minWidth: 0, wordBreak: "break-word" }}>Synchronisation en échec — {message}</span>
    </div>
  );
}

/** Bande-titre d'une section (étiquette + pastilles de synthèse) + conteneur de cartes. */
export function Band({ title, statuses, extra, children }) {
  const c = statuses ? countStatuses(statuses) : null;
  return (
    <section style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{
          background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: 8,
          padding: "7px 18px", fontWeight: 700, fontSize: 14, minWidth: 150, color: "var(--text-1)",
        }}>{title}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginLeft: "auto" }}>
          {c && (
            <>
              <Chip>{statuses.length} appareil(s)</Chip>
              <Chip color="var(--success)">{c.OK} OK</Chip>
              <Chip color="var(--warning)">{c.WARNING} avert.</Chip>
              <Chip color="var(--danger)">{c.CRITICAL} critique</Chip>
            </>
          )}
          {extra}
        </div>
      </div>
      <div style={{ border: "1px solid var(--border)", borderRadius: 14, padding: 14, background: "var(--navy-mid)" }}>
        {children}
      </div>
    </section>
  );
}

export function Chip({ children, color }) {
  return (
    <span style={{
      fontSize: 11, padding: "3px 10px", borderRadius: 20, border: "1px solid var(--border)",
      background: "var(--card-bg)", color: color || "var(--text-2)", whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

/** Grille responsive de cartes. */
export function CardGrid({ children, min = 330, maxHeight }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))`, gap: 12,
      ...(maxHeight ? { maxHeight, overflowY: "auto", paddingRight: 2 } : {}),
    }}>
      {children}
    </div>
  );
}

/** Message vide / chargement / erreur d'une section. */
export function Placeholder({ loading, error, empty }) {
  if (loading) return <div style={{ textAlign: "center", padding: 30, color: "var(--text-3)" }}><i className="bi bi-arrow-repeat spin" style={{ fontSize: 20, display: "block", marginBottom: 8 }} />Chargement…</div>;
  if (error)   return <div style={{ background: "var(--danger-dim)", border: "1px solid var(--danger)", borderRadius: 10, padding: 14, color: "var(--danger)", fontSize: 13 }}><i className="bi bi-exclamation-triangle me-2" />{error}</div>;
  return <div style={{ textAlign: "center", padding: 30, color: "var(--text-3)", fontSize: 13 }}>{empty}</div>;
}

/**
 * Corps d'une section : chargement → spinner ; erreur de rafraîchissement →
 * bandeau d'erreur SANS masquer les dernières données connues ; liste vide →
 * message. Évite qu'un raté ponctuel de réseau fasse disparaître tout l'écran.
 */
export function Section({ loading, error, count, empty, children }) {
  if (loading) return <Placeholder loading />;
  return (
    <>
      {error && <div style={{ marginBottom: count ? 12 : 0 }}><Placeholder error={error} /></div>}
      {count === 0 ? (error ? null : <Placeholder empty={empty} />) : children}
    </>
  );
}

/** Tuile de chiffre clé. */
export function KpiTile({ label, value, color = "var(--text-1)", sub }) {
  return (
    <div style={{ background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: 12, padding: "12px 16px", minWidth: 0 }}>
      <div style={{ fontSize: 11.5, color: "var(--text-3)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, fontFamily: "Syne,sans-serif", color, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** Horloge en direct (heure + date). */
export function LiveClock({ compact = false }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  // Version compacte : alignée à droite, hauteur d'une barre d'onglets.
  if (compact) {
    return (
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "Syne,sans-serif", lineHeight: 1.1, fontVariantNumeric: "tabular-nums", color: "var(--text-1)" }}>
          {now.toLocaleTimeString("fr-FR")}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--text-2)", textTransform: "capitalize", whiteSpace: "nowrap" }}>
          {now.toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 30, fontWeight: 800, fontFamily: "Syne,sans-serif", lineHeight: 1, fontVariantNumeric: "tabular-nums", color: "var(--text-1)" }}>
        {now.toLocaleTimeString("fr-FR")}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-2)", marginTop: 4, textTransform: "capitalize" }}>
        {now.toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" })}
      </div>
    </div>
  );
}

/** Indicateur "en direct" : pastille pulsante, dernière actualisation et compte à rebours. */
export function LiveIndicator({ lastRefresh, refreshing, intervalMs, onRefresh }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = lastRefresh ? Date.now() - lastRefresh.getTime() : 0;
  const ratio = Math.max(0, Math.min(1, 1 - elapsed / intervalMs));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "var(--text-2)", flexWrap: "wrap" }}>
      <span className="dash-pulse" />
      <span>
        {lastRefresh ? `Actualisé à ${lastRefresh.toLocaleTimeString("fr-FR")}${refreshing ? "…" : ""} — toutes les ${Math.round(intervalMs / 1000)} s` : "Chargement…"}
      </span>
      <div title="Prochain rafraîchissement" style={{ width: 90, height: 4, borderRadius: 3, background: "var(--navy-soft)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${ratio * 100}%`, background: "var(--accent)", transition: "width 1s linear" }} />
      </div>
      <button className="btn-itm btn-itm-outline" style={{ padding: "3px 9px", fontSize: 11 }} onClick={onRefresh} disabled={refreshing}>
        <i className={`bi bi-arrow-repeat ${refreshing ? "spin" : ""} me-1`} />Actualiser
      </button>
    </div>
  );
}

/** Styles partagés (animation, responsive) — monté une seule fois par la page. */
export function DashboardStyles() {
  return (
    <style>{`
      @keyframes spin { to { transform: rotate(360deg); } }
      .spin { animation: spin .7s linear infinite; display: inline-block; }
      .dash-pulse { width: 9px; height: 9px; border-radius: 50%; background: var(--success); animation: dashPulse 2s infinite; flex-shrink: 0; }
      @keyframes dashPulse {
        0% { box-shadow: 0 0 0 0 rgba(22,163,74,.55); }
        70% { box-shadow: 0 0 0 9px rgba(22,163,74,0); }
        100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); }
      }
      @keyframes dashTicker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      .dash-ticker-track { animation: dashTicker 40s linear infinite; }
      .dash-ticker:hover .dash-ticker-track { animation-play-state: paused; }
      @media (prefers-reduced-motion: reduce) { .dash-ticker-track, .dash-pulse { animation: none; } }
      .dash-row-btn { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left; padding: 8px 10px;
        border-radius: 8px; border: 1px solid transparent; background: transparent; cursor: pointer; font: inherit; color: var(--text-1); }
      .dash-row-btn:hover { background: var(--navy-soft); }
      .dash-row-btn.active { background: var(--navy-soft); border-color: var(--border-md); }
      .dash-cols { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; align-items: start; }
      @media (max-width: 1250px) { .dash-cols { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
      @media (max-width: 700px) { .dash-cols { grid-template-columns: minmax(0, 1fr); } }
      @media (max-width: 900px) {
        .dash-metrics-grid { grid-template-columns: repeat(2, 1fr) !important; }
        .dash-split { grid-template-columns: 1fr !important; }
        .dash-charts-grid { grid-template-columns: 1fr !important; }
      }
      @media (max-width: 400px) { .dash-metrics-grid { grid-template-columns: 1fr !important; } }
    `}</style>
  );
}